<script lang="ts">
  import { onMount, onDestroy } from "svelte";
  import { createLogSocket } from "../lib/ws";
  import type { WsMessage } from "../lib/ws";

  interface Props {
    ns: string;
    name: string;
    container: string;
    onDisconnect?: () => void;
  }

  let { ns, name, container, onDisconnect }: Props = $props();

  let terminalEl: HTMLDivElement;
  let searchInputEl: HTMLInputElement;
  let term: any;
  let searchAddon: any;
  let socket: WebSocket | null = null;

  let searchQuery = $state("");
  let searchOpen = $state(false);
  let matchCount = $state<string>("");

  onMount(() => { init(); });

  async function init() {
    const [{ Terminal }, { FitAddon }, { SearchAddon }] = await Promise.all([
      import("@xterm/xterm"),
      import("@xterm/addon-fit"),
      import("@xterm/addon-search"),
    ]);
    await import("@xterm/xterm/css/xterm.css");

    term = new Terminal({
      cursorBlink: false,
      disableStdin: true,
      fontSize: 13,
      fontFamily: "var(--font-mono)",
      scrollback: 10000,
      convertEol: true,
      theme: {
        background: "#0f1117",
        foreground: "#e1e4ed",
        cursor: "#0f1117",
        selectionBackground: "#2e3346",
      },
    });

    const fitAddon = new FitAddon();
    searchAddon = new SearchAddon();
    term.loadAddon(fitAddon);
    term.loadAddon(searchAddon);
    term.open(terminalEl);
    fitAddon.fit();

    const resizeObs = new ResizeObserver(() => fitAddon.fit());
    resizeObs.observe(terminalEl);

    socket = createLogSocket(ns, name, container);

    socket.onmessage = (event) => {
      try {
        const msg: WsMessage = JSON.parse(event.data);
        if (msg.type === "log") {
          term.write(msg.data);
        }
      } catch {
        // ignore
      }
    };

    socket.onclose = () => {
      onDisconnect?.();
    };
  }

  function toggleSearch() {
    searchOpen = !searchOpen;
    if (searchOpen) {
      requestAnimationFrame(() => searchInputEl?.focus());
    } else {
      searchQuery = "";
      matchCount = "";
      searchAddon?.clearDecorations();
    }
  }

  function doSearch(direction: "next" | "prev" = "next") {
    if (!searchAddon || !searchQuery) {
      matchCount = "";
      return;
    }
    const opts = { regex: false, caseSensitive: false, incremental: true };
    let found: boolean;
    if (direction === "prev") {
      found = searchAddon.findPrevious(searchQuery, opts);
    } else {
      found = searchAddon.findNext(searchQuery, opts);
    }
    matchCount = found ? "" : "No matches";
  }

  function handleSearchKeydown(ev: KeyboardEvent) {
    if (ev.key === "Enter") {
      ev.preventDefault();
      doSearch(ev.shiftKey ? "prev" : "next");
    }
    if (ev.key === "Escape") {
      toggleSearch();
    }
  }

  function handleSearchInput() {
    doSearch("next");
  }

  function clearLogs() {
    term?.clear();
  }

  onDestroy(() => {
    socket?.close();
    term?.dispose();
  });
</script>

<div class="log-viewer">
  <div class="log-toolbar">
    <span class="log-label">Streaming logs</span>
    <div class="toolbar-actions">
      <button class="btn-ghost" onclick={toggleSearch} class:active={searchOpen}>Search</button>
      <button class="btn-ghost" onclick={clearLogs}>Clear</button>
    </div>
  </div>

  {#if searchOpen}
    <div class="search-bar">
      <input
        bind:this={searchInputEl}
        bind:value={searchQuery}
        oninput={handleSearchInput}
        onkeydown={handleSearchKeydown}
        placeholder="Search logs..."
        class="search-input"
      />
      <button class="btn-search" onclick={() => doSearch("prev")} title="Previous (Shift+Enter)">
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M6 9V3M3.5 5.5L6 3l2.5 2.5" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/></svg>
      </button>
      <button class="btn-search" onclick={() => doSearch("next")} title="Next (Enter)">
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M6 3v6M3.5 6.5L6 9l2.5-2.5" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/></svg>
      </button>
      {#if matchCount}
        <span class="match-info">{matchCount}</span>
      {/if}
    </div>
  {/if}

  <div class="log-terminal" bind:this={terminalEl}></div>
</div>

<style>
  .log-viewer {
    display: flex;
    flex-direction: column;
    border: 1px solid var(--border);
    border-radius: 4px;
    overflow: hidden;
  }

  .log-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.35rem 0.75rem;
    background: var(--bg-surface);
    border-bottom: 1px solid var(--border);
    font-size: 0.75rem;
  }

  .log-label {
    color: var(--text-muted);
  }

  .toolbar-actions {
    display: flex;
    gap: 0.4rem;
  }

  .btn-ghost {
    background: none;
    border: 1px solid var(--border);
    color: var(--text-muted);
    padding: 0.15rem 0.5rem;
    border-radius: 3px;
    cursor: pointer;
    font-size: 0.75rem;
  }

  .btn-ghost:hover { color: var(--text); }
  .btn-ghost.active { color: var(--accent); border-color: var(--accent); }

  .search-bar {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    padding: 0.35rem 0.75rem;
    background: var(--bg-surface);
    border-bottom: 1px solid var(--border);
  }

  .search-input {
    flex: 1;
    background: var(--bg);
    border: 1px solid var(--border);
    border-radius: 3px;
    color: var(--text);
    font-family: var(--font-mono);
    font-size: 0.8rem;
    padding: 0.2rem 0.5rem;
    outline: none;
  }

  .search-input:focus { border-color: var(--accent); }

  .btn-search {
    display: flex;
    align-items: center;
    justify-content: center;
    background: none;
    border: 1px solid var(--border);
    color: var(--text-muted);
    cursor: pointer;
    padding: 0.2rem;
    border-radius: 3px;
  }

  .btn-search:hover { color: var(--accent); border-color: var(--accent); }

  .match-info {
    font-size: 0.7rem;
    color: var(--warning);
    white-space: nowrap;
  }

  .log-terminal {
    height: 500px;
    padding: 4px;
    background: var(--bg);
  }

  :global(.xterm) {
    padding: 4px;
  }
</style>
